import i18next from "i18next";
import type { FoaPart, ProcedureType, Regulation } from "../../autogen/bff-api";
import { tiptapToPlainText } from "../input/TipTap/tiptapToPlainText";
import { procedureShownAs, procedureTileKey } from "../procurement/publicProcurementOptions";
import { CREATED_FROM_NEED } from "./originatingNeed";
import { MODEL_LANGUAGE } from "./tools";
import { hiddenEventDataOf } from "./wizardHiddenFields";

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

const NOT_ABOUT_THE_STEPS = new Set([
  "operations",
  "internalConversation",
  "changes",
  "changeDraftInvitations",
  "viewings",
  "supplierRegistrations",
  "selectedProductBids",
  "devToolsAllowed",
  "documents",
  "uploads",
]);

const FIRST = [
  "savedRemovalsAndRenames",
  "id",
  "status",
  CREATED_FROM_NEED,
  "eventData",
  "plannedTenderDeadline",
  "eventContext",
  "questionDeadline",
  "milestoneDates",
];

const SET_AT_THE_INVITATION =
  "Anskaffelsen har ingen tilbudsfrist ennå: tilbudsfristen settes når invitasjonen til å gi tilbud sendes. " +
  "Datoen her er den planlagte tilbudsfristen, slik steget «Prosedyre» viser den. Oppgi den som planlagt og satt " +
  "når invitasjonen sendes, og oppgi aldri en annen dato som anskaffelsens tilbudsfrist.";


export const sourcingEventForModel = (event: unknown): unknown => {
  const kept = withoutNoise(withTenderDeadlineByTheRule(withProcedureName(withoutHiddenToggles(event))) as Json) ?? {};
  if (typeof kept !== "object" || Array.isArray(kept)) return kept;
  const ordered: { [key: string]: Json } = {};
  FIRST.filter((key) => key in kept).forEach((key) => (ordered[key] = kept[key]));
  Object.keys(kept)
    .filter((key) => !(key in ordered))
    .forEach((key) => (ordered[key] = kept[key]));
  return ordered;
};

const withoutHiddenToggles = (event: unknown): unknown => {
  if (!event || typeof event !== "object") return event;
  const { eventData } = event as { eventData?: Record<string, unknown> };
  const hidden = hiddenEventDataOf(event);
  if (!eventData || hidden.length === 0) return event;
  const shown = Object.fromEntries(Object.entries(eventData).filter(([key]) => !hidden.includes(key)));
  return { ...event, eventData: shown };
};

const withTenderDeadlineByTheRule = (event: unknown): unknown => {
  if (!isRow(event) || !isRow(event.eventData) || !isRow(event.milestoneDates)) return event;
  const { bidSubmissionDeadline, plannedTenderDeadline } = event.milestoneDates;
  if (bidSubmissionDeadline) return { ...event, eventData: { ...event.eventData, bidSubmissionDeadline } };
  const eventData = { ...event.eventData, bidSubmissionDeadline: null, bidSubmissionDeadlineConfirmed: null };
  if (!plannedTenderDeadline) return { ...event, eventData };
  return { ...event, eventData, plannedTenderDeadline: { at: plannedTenderDeadline, note: SET_AT_THE_INVITATION } };
};

const withProcedureName = (event: unknown): unknown => {
  if (!event || typeof event !== "object") return event;
  const { eventContext } = event as { eventContext?: Record<string, unknown> };
  if (eventContext?.type !== "Public" || typeof eventContext.procedureType !== "string") return event;
  const shownAs = procedureShownAs(
    eventContext.procedureType as ProcedureType,
    eventContext.regulation as Regulation | undefined,
    eventContext.foaPart as FoaPart | undefined,
  );
  const procedureName = shownAs ? String(i18next.t(procedureTileKey(shownAs), { lng: MODEL_LANGUAGE })) : undefined;
  return { ...event, eventContext: { ...eventContext, procedureName } };
};

const withoutNoise = (value: Json): Json | undefined => {
  if (value === null) return undefined;
  if (typeof value === "string") {
    const text = value.trimStart().startsWith('{"type":"doc"') ? tiptapToPlainText(value) : value;
    return text === "" ? undefined : text;
  }
  if (Array.isArray(value)) {
    const items = value.map(withoutNoise).filter((item): item is Json => item !== undefined);
    return items.length ? items : undefined;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value)
      .filter(([key]) => !NOT_ABOUT_THE_STEPS.has(key))
      .map(([key, field]) => [key, withoutNoise(field)] as const)
      .filter((entry): entry is readonly [string, Json] => entry[1] !== undefined);
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  return value;
};

const isRow = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
